import { setCommunityData, setOCPData, setPendingBillData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetCommunitiesDropdown,
  useGetOwnershipChangePacketByCommunity,
  useGetPropertiesByCommunity,
} from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { OCP, ocpStatus, PROPERTY_NAME, REPORTED_ON, REPORTER, STATUS } from "./consts";
import moment from "moment";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui";
import { Edit, MoreVertical } from "lucide-react";

const useOwnershipChangePacket = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [globalFilter, setGlobalFilter] = useState("");
  const [communityKey, setCommunityKey] = useState<number>(Math.random() * 2);

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunitiesDropdown({});

  const {
    data: ownershipChangePacketData,
    isLoading: isOwnershipChangePacketLoading,
  } = useGetOwnershipChangePacketByCommunity({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId,
    },
  });

  const { data: propertyList } = useGetPropertiesByCommunity({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId,
    },
  });

  useEffect(() => {
    if (communityList) {
      if (communityData) {
        setCommunityKey(Math.random() * 2);
      } else {
        dispatch(setCommunityData(communityList?.[0]));
        setCommunityKey(Math.random() * 2);
      }
    }
  }, [communityList]);

  const propertiesArray = useMemo(() => propertyList?.data || [], [propertyList]);

  const propertyMap = useMemo(() => {
    const map = new Map<string, string>();
    propertiesArray.forEach((prop: any) => {
      if (prop?.propertyId) {
        map.set(String(prop.propertyId), prop.name || "");
      }
    });
    return map;
  }, [propertiesArray]);

  const columns = useMemo(
    () => [
      {
        accessorKey: OCP,
        header: <div>OCP</div>,
        accessorFn: (row: any) => String(row?.[OCP] || ""),
      },
      {
        accessorKey: PROPERTY_NAME,
        header: <div>Property Name</div>,
        cell: ({ row }: any) => {
          const propertyId = row?.original?.propertyId;
          if (!propertyId) return "-";
  
          // Dynamic lookup inside cell render ensures reactivity when propertyMap updates
          return propertyMap.get(String(propertyId)) || "-";
        },
      },
      {
        accessorKey: REPORTER,
        header: <div>Reporter</div>,
        cell: ({row}: any) => {
          const reporterName = row?.original?.[REPORTER];

          return <span title={reporterName} // Shows full text on hover
            className="inline-block max-w-[10ch] truncate align-middle">
            {String(reporterName || "")}
          </span>
        }
      },
      {
        accessorKey: STATUS,
        header: <div>Status</div>,
        accessorFn: (row: any) => (ocpStatus as any)[row?.[STATUS]] || "",
      },
      {
        accessorKey: REPORTED_ON,
        header: <div>Receipt Date</div>,
        accessorFn: (row: any) => row?.[REPORTED_ON] ? moment(row?.[REPORTED_ON]).format("MM/DD/YYYY") : "",
      },
      {
        id: "actions",
        header: <div>Actions</div>,
        cell: ({row}: any) => (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                disabled={row?.original?.status?.toLowerCase() == "owner_onboarded"}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  dispatch(setOCPData(null));
                  dispatch(setPendingBillData(null));
                  navigate(`${row.original?.[OCP]}/edit`);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [propertyMap]
  );

  const onCreateNew = () => {
    dispatch(setOCPData(null));
    dispatch(setPendingBillData(null));
    navigate(routes.add);
  };

  const onCommunityChange = (value: string) => {
    const details = communityList?.find(
      (data: any) => data.communityId == value
    );
    dispatch(setCommunityData(details));
  };

  const onRowClick = (row: any) => {
    dispatch(setOCPData(null));
    dispatch(setPendingBillData(null));
    navigate(`${row.original?.[OCP]}/view`);
  }

  return {
    columns,
    globalFilter,
    setGlobalFilter,
    ownershipChangePacketData: ownershipChangePacketData?.data || [],
    onCreateNew,
    communityList,
    communityKey,
    onCommunityChange,
    isCommunityLoading,
    isOwnershipChangePacketLoading,
    communityData,
    onRowClick
  };
};

export default useOwnershipChangePacket;
